/**
 * Connection Details — the full-page connect screen.
 *
 * Two columns. The left one is the form, in the order someone fills it in:
 * name the connection, file it (workspace and tags), choose how to
 * authenticate, then supply what that choice asked for. The right one is the
 * connector's own documentation, open beside the form rather than behind a
 * disclosure, because the credentials being asked for are usually somewhere in
 * it.
 *
 * As with the dialog it replaces, there is no per-connector code here. Every
 * field, every auth method and every setup step comes from the manifest.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAuthFlow, useManifest } from '@/core/hooks'
import { connectionsApi } from '@/core/api/connections.api'
import { useToast } from '@/shared/components/toast'
import { Callout, ErrorNotice, Loading, Logo } from '@/shared/components'
import { formatError } from '@/core/utils/error.formatter'
import { IdentityButton, brandFor } from './IdentityButton'
import { CredentialInput } from './CredentialInput'
import {
  CircuitBreakerFields,
  RateLimitFields,
  TagsField,
  WorkspaceField,
  emptySettings,
  toSettingsPayload,
  type ConnectionSettingsValue,
} from './ConnectionSettingsFields'
import type { AuthMethod } from '@/core/types/api.types'

/** What a non-HTTP transport means for whoever is connecting. */
const TRANSPORT_NOTES: Record<string, string> = {
  oracle_sql: 'Oracle',
  postgres_sql: 'PostgreSQL',
  mongodb: 'MongoDB',
}

export function ConnectPage() {
  const { connectorId = '' } = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const manifest = useManifest(connectorId)
  const flow = useAuthFlow()
  const toast = useToast()

  const connector = manifest.data
  const methods: AuthMethod[] = useMemo(() => connector?.auth_methods ?? [], [connector])

  const [label, setLabel] = useState('')
  const [methodId, setMethodId] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, unknown>>({})
  const [settings, setSettings] = useState<ConnectionSettingsValue>(emptySettings)

  // Suggestions come from what already exists, so the second connection in a
  // workspace is a pick rather than a retyped string.
  const [known, setKnown] = useState<{ workspaces: string[]; tags: string[] }>({
    workspaces: [],
    tags: [],
  })
  useEffect(() => {
    let cancelled = false
    connectionsApi
      .listing()
      .then((d) => {
        if (!cancelled) setKnown({ workspaces: d.workspaces ?? [], tags: d.tags ?? [] })
      })
      // Suggestions are a convenience; failing to load them must not block the
      // form, which works perfectly well with free text.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  // Default to the first method that can actually be used, so a connector whose
  // OAuth app is unconfigured opens on the option that works.
  useEffect(() => {
    if (methodId || methods.length === 0) return
    setMethodId((methods.find((m) => m.ready) ?? methods[0]).id)
  }, [methods, methodId])

  const method = methods.find((m) => m.id === methodId) ?? null

  // Switching method means a different field set; carrying values across would
  // submit leftovers from the other option.
  useEffect(() => {
    setFields({})
  }, [methodId])

  const test = useMutation({
    mutationFn: () => connectionsApi.testConnection(connectorId, methodId, fields),
  })

  // A passing test is only meaningful for the values that produced it.
  useEffect(() => {
    test.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, methodId])

  const missingRequired = useMemo(() => {
    if (!method) return []
    return method.fields
      .filter((f) => f.required)
      .filter((f) => {
        const v = fields[f.key]
        return v === undefined || v === null || (typeof v === 'string' && !v.trim())
      })
      .map((f) => f.label)
  }, [method, fields])

  const isOAuth = method?.type === 'oauth2'
  const brand = brandFor(method)
  const canSubmit = Boolean(method?.ready) && missingRequired.length === 0

  async function submit() {
    if (!connector || !method) return
    try {
      const connection = await flow.mutateAsync({
        connector,
        methodId: method.id,
        label: label.trim() || undefined,
        fields,
        scopes: undefined,
        settings: toSettingsPayload(settings),
      })
      toast.success('Connected', `${connection.label} is ready to use.`)
      // A `return` in the query means someone was sent here mid-flow — from the
      // source wizard, say — so they go back to what they were doing rather
      // than landing on a detail page they did not ask for.
      const back = search.get('return')
      navigate(back ? back : `/connections/${connection.connection_id}`)
    } catch (err) {
      toast.error(err, 'Could not connect')
    }
  }

  if (manifest.isLoading) return <Loading label="Reading the manifest…" />
  if (manifest.error) {
    return <ErrorNotice error={manifest.error} onRetry={() => manifest.refetch()} />
  }
  if (!connector) return null

  return (
    <div className="stack">
      <div className="page-header page-header-row">
        <div className="row" style={{ gap: 11, minWidth: 0 }}>
          <Logo src={connector.logo} name={connector.name} id={connector.id} />
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0 }}>{connector.name}</h2>
            <p style={{ margin: '4px 0 0' }}>{connector.description}</p>
          </div>
        </div>

        {/* OAuth has nothing to test before the round trip — the credential
            does not exist until the provider issues it. */}
        {!isOAuth && method && method.fields.length > 0 && (
          <button
            className="btn"
            onClick={() => test.mutate()}
            disabled={test.isPending || missingRequired.length > 0}
          >
            {test.isPending && <span className="spinner" />}
            {test.isPending ? 'Testing…' : 'Test Connection'}
          </button>
        )}
      </div>

      <div className="connect-layout">
        {/* ---------------------------------------------------------- form */}
        <div className="card stack">
          <div>
            <h3 style={{ margin: 0 }}>Connection Details</h3>
            <p className="small muted" style={{ margin: '4px 0 0' }}>
              Configure authentication and settings for this connection.
            </p>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="conn-label">
              Connection name<span className="field-req">*</span>
            </label>
            <input
              id="conn-label"
              className="input"
              value={label}
              placeholder={`e.g. ${connector.id}-production`}
              onChange={(e) => setLabel(e.target.value)}
            />
            <p className="field-help">
              Name your connection. This name is shown upfront in all automations. Left blank, it
              is named from the account it connects to.
            </p>
          </div>

          <WorkspaceField
            value={settings.workspaceId}
            options={known.workspaces}
            onChange={(workspaceId) => setSettings((s) => ({ ...s, workspaceId }))}
          />

          <TagsField
            value={settings.tags}
            suggestions={known.tags}
            onChange={(tags) => setSettings((s) => ({ ...s, tags }))}
          />

          <div className="field">
            <label className="field-label" htmlFor="conn-auth-type">
              Authentication type<span className="field-req">*</span>
            </label>
            <select
              id="conn-auth-type"
              className="select"
              value={methodId ?? ''}
              onChange={(e) => setMethodId(e.target.value)}
            >
              {methods.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                  {m.ready ? '' : ' — setup needed'}
                </option>
              ))}
            </select>
            {method?.description && <p className="field-help">{method.description}</p>}
          </div>

          {method && (
            <>
              {/* Naming the exact variables turns "ask an administrator" into
                  something a person can act on. */}
              {!method.ready && (
                <Callout kind="warn" title="This option needs server configuration">
                  <p className="small">{method.setup_hint}</p>
                  {method.required_env.length > 0 && (
                    <p className="small mono" style={{ marginTop: 5 }}>
                      {method.required_env.join('  ·  ')}
                    </p>
                  )}
                  {methods.some((m) => m.ready && m.id !== method.id) && (
                    <p className="small" style={{ marginTop: 5 }}>
                      Another authentication type above works without this.
                    </p>
                  )}
                </Callout>
              )}

              {isOAuth && method.ready && (
                <Callout kind="accent">
                  <p className="small">
                    Opens {connector.name} in a new window to approve access. Nothing is stored
                    until you approve.
                  </p>
                  {method.app_from_connection && (
                    <p className="small" style={{ marginTop: 5 }}>
                      This option uses your own OAuth app, so fill in its details below first —
                      they are needed to build the sign-in link, and are kept so the connection
                      can refresh itself later.
                    </p>
                  )}
                  {method.scopes.length > 0 && (
                    <div className="row" style={{ gap: 4, marginTop: 7 }}>
                      {method.scopes.map((s) => (
                        <span key={s} className="badge badge-neutral mono">
                          {s.replace(/^https:\/\/www\.googleapis\.com\/auth\//, '')}
                        </span>
                      ))}
                    </div>
                  )}
                </Callout>
              )}

              {method.transport !== 'http' && (
                <Callout kind="warn" title="Opens a database session">
                  <p className="small">
                    This reaches {TRANSPORT_NOTES[method.transport] ?? 'the database'} directly
                    over TCP rather than through an HTTP API, so the server running this app must
                    be able to reach it — and what the connection can do is exactly what the
                    database user it signs in as is allowed to do.
                  </p>
                </Callout>
              )}

              {method.fields.length > 0 && (
                <div className="stack" style={{ gap: 13 }}>
                  {method.fields.map((f) => (
                    <CredentialInput
                      key={f.key}
                      field={f}
                      value={fields[f.key]}
                      onChange={(v) => setFields((prev) => ({ ...prev, [f.key]: v }))}
                    />
                  ))}
                </div>
              )}

              {method.type === 'none' && (
                <Callout kind="accent">
                  <p className="small">
                    {connector.name} needs no credentials. Connecting creates an id you can run
                    actions against.
                  </p>
                </Callout>
              )}
            </>
          )}

          <div className="divider" />

          <RateLimitFields
            value={settings.rateLimit}
            onChange={(rateLimit) => setSettings((s) => ({ ...s, rateLimit }))}
          />
          <CircuitBreakerFields
            value={settings.circuitBreaker}
            onChange={(circuitBreaker) => setSettings((s) => ({ ...s, circuitBreaker }))}
          />

          {test.data && (
            <Callout
              kind={test.data.ok ? 'ok' : 'danger'}
              title={test.data.ok ? 'Credentials accepted' : 'Test failed'}
            >
              <p className="small">{test.data.message}</p>
              {!test.data.checked && (
                <p className="small faint">
                  Nothing was verified — this connector declares no check endpoint.
                </p>
              )}
            </Callout>
          )}
          {test.error ? (
            <Callout kind="danger" title="Test failed">
              <p className="small">{formatError(test.error).message}</p>
            </Callout>
          ) : null}

          {missingRequired.length > 0 && (
            <p className="small faint" style={{ margin: 0 }}>
              Still needed: {missingRequired.join(', ')}
            </p>
          )}

          {flow.error ? <ErrorNotice error={flow.error} /> : null}

          <div className="row-tight" style={{ justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => navigate(-1)} disabled={flow.isPending}>
              Cancel
            </button>
            {/* Google and Microsoft publish how their sign-in button must look,
                and the recognisable mark is also what tells someone where they
                are about to hand over access. */}
            {brand && isOAuth ? (
              <IdentityButton
                brand={brand}
                busy={flow.isPending}
                disabled={flow.isPending || !canSubmit}
                onClick={submit}
              />
            ) : (
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={flow.isPending || !canSubmit}
              >
                {flow.isPending && <span className="spinner" />}
                {flow.isPending
                  ? isOAuth
                    ? 'Waiting for approval…'
                    : 'Creating…'
                  : isOAuth
                    ? `Continue to ${connector.name}`
                    : 'Create'}
              </button>
            )}
          </div>
        </div>

        {/* ---------------------------------------------------------- docs */}
        <aside className="card stack connect-docs">
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0 }}>{connector.name} Docs</h3>
              <p className="small muted" style={{ margin: '4px 0 0' }}>
                Setup instructions and reference for this connector.
              </p>
            </div>
            {connector.docs_url && (
              <a
                className="btn btn-sm"
                href={connector.docs_url}
                target="_blank"
                rel="noreferrer noopener"
              >
                Go to documentation ↗
              </a>
            )}
          </div>

          <div className="divider" />

          {method ? (
            <>
              <h4 style={{ margin: 0 }}>{method.label}</h4>
              {method.description && <p className="small muted">{method.description}</p>}

              {method.setup_steps.length > 0 ? (
                <ol className="setup-steps">
                  {method.setup_steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              ) : (
                <p className="small faint">
                  This method declares no setup steps — the fields on the left are all it needs.
                </p>
              )}

              {method.setup_url && (
                <a
                  className="small"
                  href={method.setup_url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Open the {connector.name} setup screen ↗
                </a>
              )}

              {method.scopes.length > 0 && (
                <>
                  <div className="divider" />
                  <h4 style={{ margin: 0 }}>Permissions requested</h4>
                  <div className="chiprow">
                    {method.scopes.map((s) => (
                      <span key={s} className="badge badge-neutral mono">
                        {s}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="small faint">Choose an authentication type to see its instructions.</p>
          )}
        </aside>
      </div>
    </div>
  )
}
